import React, { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { useOrganization } from '../contexts/OrganizationContext'
import api from '../api/client'
import type { Shift } from '../types'
import { Users, Calendar, UserCheck, Clock, Plus } from 'lucide-react'
import { Alert, Button, Capacity, Card } from '@rallyshift/design-system'
import { formatDateTimeToLocal } from '../utils/timezone'
import { CAPACITY_LABELS } from '../utils/status'
import { useNavigate } from 'react-router-dom'
import VolunteerActivityTimeline, { ActivityItem } from '../components/VolunteerActivityTimeline'

const Stat: React.FC<{
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: React.ReactNode
}> = ({ icon: Icon, label, value }) => (
  <Card>
    <div className="flex items-center">
      <Icon className="h-6 w-6 flex-shrink-0 text-text-muted" />
      <div className="ml-5 w-0 flex-1">
        <dl>
          <dt className="truncate text-sm font-semibold text-text-secondary">{label}</dt>
          <dd className="font-display text-display-sm text-text-primary">{value}</dd>
        </dl>
      </div>
    </div>
  </Card>
)

const DashboardPage: React.FC = () => {
  const { profile, isVolunteer, isAdmin, user, volunteerRequest, refreshProfile } = useAuth()
  const { organization } = useOrganization()
  const { t } = useLocale()
  const navigate = useNavigate()
  const [volunteerCount, setVolunteerCount] = useState(0)
  const [shiftCount, setShiftCount] = useState(0)
  const [upcomingShifts, setUpcomingShifts] = useState<Shift[]>([])
  const [loading, setLoading] = useState(true)

  // Admin-specific metrics
  const [totalSeatsFilled, setTotalSeatsFilled] = useState(0)
  const [totalSeatsAvailable, setTotalSeatsAvailable] = useState(0)
  const [filledShiftsCount, setFilledShiftsCount] = useState(0)
  const [totalShiftsCount, setTotalShiftsCount] = useState(0)
  const [recentActivities, setRecentActivities] = useState<ActivityItem[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)

  const addToCalendar = (shift: Shift) => {
    const formatForCalendar = (date: Date) =>
      date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

    const title = encodeURIComponent(shift.title)
    const startTime = formatForCalendar(new Date(shift.start_time))
    const endTime = formatForCalendar(new Date(shift.end_time))
    const location = encodeURIComponent(
      `${shift.location}${shift.event?.title ? ` - ${shift.event.title}` : ''}`,
    )
    const description = encodeURIComponent(shift.description || shift.title)

    window.open(
      `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startTime}/${endTime}&location=${location}&details=${description}`,
      '_blank',
      'noopener',
    )
  }

  const fetchRecentActivities = useCallback(async () => {
    if (!isAdmin) return

    setActivitiesLoading(true)
    try {
      const pending = (await api.shifts.getPendingAssignments()) || []
      const activities: ActivityItem[] = pending
        .filter((a: any) => a.volunteer && a.shift)
        .map((assignment: any) => ({
          id: `shift-${assignment.id}`,
          type: 'shift_claimed',
          timestamp: assignment.assigned_at,
          volunteer_name: `${assignment.volunteer.first_name || ''} ${
            assignment.volunteer.last_name || ''
          }`.trim(),
          volunteer_email: assignment.volunteer.email,
          volunteer_avatar: assignment.volunteer.avatar_url,
          description: '',
          metadata: {
            shift_title: assignment.shift.title,
            shift_time: `${formatDateTimeToLocal(
              assignment.shift.start_time,
            )} - ${formatDateTimeToLocal(assignment.shift.end_time)}`,
            shift_location: assignment.shift.location,
            status: assignment.status,
          },
        }))

      activities.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      )
      setRecentActivities(activities.slice(0, 10))
    } catch (error) {
      console.error('Error fetching recent activities:', error)
    } finally {
      setActivitiesLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (isVolunteer || isAdmin) {
          const [volunteers, shiftsList, myAssignments] = await Promise.all([
            api.volunteerMetadata.list(),
            api.shifts.list(),
            user ? api.shifts.getMyAssignments() : Promise.resolve([]),
          ])

          setVolunteerCount(volunteers?.length ?? 0)
          setShiftCount(shiftsList?.length ?? 0)

          const now = new Date().toISOString()
          const upcoming = (myAssignments || [])
            .filter((a: any) => a.shift && a.shift.start_time >= now && a.status !== 'cancelled')
            .sort(
              (a: any, b: any) =>
                new Date(a.shift.start_time).getTime() - new Date(b.shift.start_time).getTime(),
            )
            .slice(0, 5)
            .map((a: any) => a.shift)
          setUpcomingShifts(upcoming)

          if (isAdmin && shiftsList) {
            const active = shiftsList.filter((s: any) => s.status === 'active')
            let totalFilled = 0
            let totalAvailable = 0
            let filledCount = 0
            active.forEach((s: any) => {
              totalFilled += s.current_volunteers ?? 0
              totalAvailable += s.max_volunteers ?? 0
              if ((s.current_volunteers ?? 0) >= (s.max_volunteers ?? 1)) filledCount++
            })
            setTotalSeatsFilled(totalFilled)
            setTotalSeatsAvailable(totalAvailable)
            setFilledShiftsCount(filledCount)
            setTotalShiftsCount(active.length)
          }
        }
        fetchRecentActivities()
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchDashboardData()
  }, [user, isVolunteer, isAdmin, fetchRecentActivities])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-tenant-brand border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-display-lg text-text-primary">
          {t('dashboard.welcomeBack').replace('{name}', profile?.first_name || '')}
        </h1>
        <p className="mt-1 text-md text-text-secondary">
          {organization ? `${organization.name} — ` : ''}
          {t('dashboard.subtitle')}
        </p>
      </div>

      {(isVolunteer || isAdmin) && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Aantal vrijwilligers" value={volunteerCount} />
          <Stat icon={Calendar} label="Actieve diensten" value={shiftCount} />
          <Stat icon={Clock} label="Mijn diensten" value={upcomingShifts.length} />
          <Stat
            icon={UserCheck}
            label="Jouw rol"
            value={<span className="capitalize">{profile?.role}</span>}
          />
        </div>
      )}

      {isAdmin && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card
            title="Diensten volledig gevuld"
            meta="Hoeveel diensten geen open plekken meer hebben"
          >
            <Capacity
              value={filledShiftsCount}
              max={totalShiftsCount}
              stateLabels={CAPACITY_LABELS}
            />
          </Card>
          <Card title="Dienst bezetting" meta="Bezette plekken over alle actieve diensten">
            <Capacity
              value={totalSeatsFilled}
              max={totalSeatsAvailable}
              stateLabels={CAPACITY_LABELS}
            />
          </Card>
        </div>
      )}

      {isAdmin && (
        <VolunteerActivityTimeline activities={recentActivities} loading={activitiesLoading} />
      )}

      {isVolunteer && (
        <Card title="Jouw toekomstige diensten">
          {upcomingShifts.length > 0 ? (
            <ul className="divide-y divide-border">
              {upcomingShifts.map((shift) => (
                <li key={shift.id} className="py-3 sm:py-4">
                  <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
                    <div className="flex min-w-0 flex-1 items-start space-x-3 sm:space-x-4">
                      <Calendar className="mt-0.5 h-5 w-5 flex-shrink-0 text-text-muted" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-md font-semibold text-text-primary">
                          {shift.title}
                        </p>
                        <p className="truncate text-sm text-text-muted">
                          {shift.event?.title} • {shift.location}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end sm:gap-1">
                      <div className="text-sm text-text-secondary sm:text-right">
                        <div>{formatDateTimeToLocal(shift.start_time, 'd MMM H:mm')}</div>
                        <div className="text-xs text-text-muted">
                          tot {formatDateTimeToLocal(shift.end_time, 'H:mm')}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        onClick={() => addToCalendar(shift)}
                        icon={<Plus className="h-4 w-4" />}
                      >
                        Agenda
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-6 text-center">
              <Calendar className="mx-auto h-12 w-12 text-text-muted" />
              <h3 className="mt-2 text-md font-semibold text-text-primary">
                {t('dashboard.noAssignedShifts')}
              </h3>
              <p className="mt-1 text-sm text-text-secondary">
                {t('dashboard.noAssignedShiftsMessage')}
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Approved, but the session still shows the old role until it is refreshed. */}
      {!isVolunteer && volunteerRequest?.status === 'approved' && profile?.role === 'user' && (
        <Alert variant="success" title="Je aanmelding is goedgekeurd">
          <p>Vernieuw je profiel om toegang te krijgen tot de diensten.</p>
          <div className="mt-4">
            <Button
              onClick={async () => {
                await refreshProfile()
                window.location.reload()
              }}
              icon={<UserCheck className="h-4 w-4" />}
            >
              Profiel vernieuwen
            </Button>
          </div>
        </Alert>
      )}

      {!isVolunteer && !(volunteerRequest?.status === 'approved' && profile?.role === 'user') && (
        <Alert variant="info" title={t('dashboard.volunteerAccessRequired')}>
          <p>{t('dashboard.volunteerAccessMessage')}</p>
          <div className="mt-4">
            <Button
              variant="tenant"
              onClick={() => navigate('/complete-volunteer-application')}
              icon={<Plus className="h-4 w-4" />}
            >
              {t('dashboard.applyAsVolunteer')}
            </Button>
          </div>
        </Alert>
      )}
    </div>
  )
}

export default DashboardPage
